// De inhoudstabel op het gezondheidsbord: welke soort inhoud zit er in de kennisbank,
// per propositie. Staat naast de meter "Cijferrijk" (BR5). De server telt en kleurt (kleur, woord,
// doel, reden); dit scherm toont alleen. Geen codes: woorden. Een cel zonder getal laat
// zijn reden zien (zichtbaar, niet alleen als tooltip).

import { Card } from "../../components/ui";
import type { GezondheidsbordResponse } from "../../contracts";
import { color, size, weight } from "../../tokens";
import { Pil, cijfer, getal, kop, kort, maand } from "./GezondheidStukken";

type Tabel = NonNullable<GezondheidsbordResponse["inhoudstabel"]>;
type Sector = GezondheidsbordResponse["sectoren"][number];

export function GezondheidInhoud({ tabel, sectoren }: { tabel: Tabel; sectoren: Sector[] }) {
  const rijen = tabel.rijen;
  if (rijen.length === 0) return null;
  const van = maand(tabel.periode);
  return (
    <section aria-label="Welke soort inhoud zit er in de kennisbank">
      <h2 style={{ fontSize: size.body, fontWeight: weight.medium, margin: "0 0 4px" }}>
        Welke soort inhoud zit er in de kennisbank?
      </h2>
      <p style={{ margin: "0 0 10px", color: color.faint, fontSize: size.label }}>
        {tabel.uitleg}
        {van ? ` Maand: ${van}.` : ""}
      </p>
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table
            aria-label="Soorten inhoud per propositie"
            style={{ width: "100%", minWidth: 720, borderCollapse: "collapse" }}
          >
            <thead>
              <tr>
                <th scope="col" style={{ ...kop, minWidth: 220 }}>
                  Soort inhoud
                </th>
                <th scope="col" style={{ ...kop, minWidth: 90 }}>
                  Doel
                </th>
                {sectoren.map((s) => (
                  <th key={s.sleutel} scope="col" style={{ ...kop, minWidth: 110 }}>
                    {kort(s.naam)}
                    {s.ondergrens ? (
                      <span style={{ display: "block", textTransform: "none", letterSpacing: 0 }}>
                        ondergrens
                      </span>
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rijen.map((r, i) => {
                const nieuweGroep = i === 0 || rijen[i - 1]?.inhoud !== r.inhoud;
                return (
                  <tr
                    key={r.code}
                    data-rij={r.inhoud}
                    style={{ borderTop: `1px solid ${color.lineFaint}` }}
                  >
                    <th scope="row" style={{ textAlign: "left", padding: "9px 12px", verticalAlign: "top" }}>
                      {nieuweGroep ? (
                        <span style={{ display: "block", fontWeight: weight.medium }}>{r.inhoud}</span>
                      ) : null}
                      <span
                        style={{ display: "block", fontWeight: weight.regular, color: color.faint, fontSize: size.label }}
                      >
                        {r.telling}
                      </span>
                    </th>
                    <td style={{ padding: "9px 12px", verticalAlign: "top", ...cijfer, color: color.faint }}>
                      {r.doelTekst ?? "geen doel"}
                    </td>
                    {sectoren.map((s) => {
                      const c = r.per[s.sleutel];
                      if (!c) {
                        return (
                          <td key={s.sleutel} style={{ padding: "9px 12px", color: color.faint }}>
                            nog niet gemeten
                          </td>
                        );
                      }
                      return (
                        <td key={s.sleutel} data-sector={s.sleutel} style={{ padding: "9px 12px", verticalAlign: "top" }}>
                          <span style={{ ...cijfer, fontWeight: weight.medium }}>{getal(c.waarde, "%")}</span>{" "}
                          <Pil kleur={c.stand}>{c.standWoord}</Pil>
                          {c.reden ? (
                            <span
                              data-reden={c.waarde === null ? "leeg" : "verouderd"}
                              style={{ display: "block", marginTop: 3, color: color.faint, fontSize: size.label }}
                            >
                              {c.reden}
                            </span>
                          ) : c.teller !== null && c.noemer !== null ? (
                            <span style={{ display: "block", marginTop: 3, color: color.faint, fontSize: size.label, ...cijfer }}>
                              {c.teller.toLocaleString("nl-NL")} van {c.noemer.toLocaleString("nl-NL")}
                            </span>
                          ) : null}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </section>
  );
}
